// Global scanner-status EventSource client.
//
// One EventSource per session, owned by this module. Mirrors the pattern of
// scanningClient.ts so a single connection serves every component on the page
// (Dashboard live cards, MapView SensorPanel, AlarmZones list, etc.) without
// stacking per-mount sockets.
//
// Backend contract (../../Server/sse/scanner-sse/src/app.ts):
//   GET https://ws.airplate.dk/scanner-events/events/scanners
//     ?org_id=<id>&authToken=<firebase id token>
//   Events:
//     scanner_state   { scanners: ScannerInfo[], sentAt }  — full snapshot on connect
//     scanner_checkin ScannerInfo                          — single upsert per check-in
//   Reconnect: server sends `retry: 3000` and replays via Last-Event-ID.

import { auth } from "../firebaseConfig";
import { ScannerSSEbase } from "../APIClient";
import { getScannerSseHostOverride } from "../utils/devScannerSse";

export type ScannerStreamStatus =
  | "idle"
  | "connecting"
  | "live"
  | "reconnecting"
  | "offline";

export type ScannerLiveStatus =
  | "active"
  | "missed_checkin"
  | "stale"
  | "inactive";

export interface ScannerStreamInfo {
  imei: string;
  status: ScannerLiveStatus;
  lastSeen: string;
  hoursSinceSeen: number;
  rssi: number | null;
  signalBars: 0 | 1 | 2 | 3 | 4;
  batteryVoltage: number;
  charging: number;
  raspConnected: boolean;
  e200Connected: boolean;
  teleOperatorName: string;
  organizationIds: string;
  scannerNames: string;
  location: string;
  version: string;
}

export type ScannerStreamSnapshot = {
  scannersByImei: Map<string, ScannerStreamInfo>;
  status: ScannerStreamStatus;
  lastSnapshotAt: number | null;
};

type Subscriber = (snapshot: ScannerStreamSnapshot) => void;

const subscribers = new Set<Subscriber>();
const scannersByImei = new Map<string, ScannerStreamInfo>();

let es: EventSource | null = null;
let currentOrgId: string | null = null;
let status: ScannerStreamStatus = "idle";
let lastSnapshotAt: number | null = null;
// Bumped on every connect()/stop(); deferred async work re-checks this so a
// superseded attempt can abort cleanly without leaking an EventSource.
let connectGeneration = 0;
let unsubscribeIdToken: (() => void) | null = null;

function emit(): void {
  const snapshot: ScannerStreamSnapshot = {
    scannersByImei: new Map(scannersByImei),
    status,
    lastSnapshotAt,
  };
  subscribers.forEach((fn) => {
    try {
      fn(snapshot);
    } catch (err) {
      console.warn("[scannerStream] subscriber threw:", err);
    }
  });
}

function setStatus(next: ScannerStreamStatus): void {
  if (status === next) return;
  status = next;
  emit();
}

async function connect(): Promise<void> {
  if (!currentOrgId) return;
  const generation = ++connectGeneration;

  // Tear down any previous connection before building a new one — handles the
  // token-rotation path (close + reopen with fresh token) and protects against
  // racing focus events stacking sockets.
  if (es) {
    try {
      es.close();
    } catch {
      // already closed
    }
    es = null;
  }

  const user = auth.currentUser;
  if (!user) {
    setStatus("offline");
    return;
  }

  setStatus(status === "live" ? "reconnecting" : "connecting");

  let token: string;
  try {
    token = await user.getIdToken();
  } catch (err) {
    console.warn("[scannerStream] getIdToken failed:", err);
    setStatus("offline");
    return;
  }

  if (generation !== connectGeneration || !currentOrgId) {
    // Superseded by stop() or another connect(); drop the token.
    return;
  }

  // ScannerSSEbase already carries the right prefix for the current mode:
  // `<api>/scanner-events/` when proxied (Traefik strips it downstream), or
  // scanner-sse's own origin when VITE_SSE_HOST points straight at it — that
  // service serves /events/scanners with no prefix. Deployed behaviour is
  // unchanged. Auth is the `authToken` query param below rather than a cookie,
  // so a cross-origin EventSource needs no credentials.
  // Dev-only escape hatch on top: on a dev host the developer can redirect
  // just this stream to the production sensor SSE (see devScannerSse) — that
  // is always an edge-proxied host, so the `scanner-events/` prefix applies.
  const sseOverride = getScannerSseHostOverride();
  const base = sseOverride ? `https://${sseOverride}/scanner-events/` : ScannerSSEbase;
  const url =
    `${base}events/scanners` +
    `?org_id=${encodeURIComponent(currentOrgId)}` +
    `&authToken=${encodeURIComponent(token)}`;

  let next: EventSource;
  try {
    next = new EventSource(url);
  } catch (err) {
    console.warn("[scannerStream] EventSource ctor failed:", err);
    setStatus("offline");
    return;
  }
  es = next;

  next.addEventListener("scanner_state", (event) => {
    if (es !== next) return;
    try {
      const payload = JSON.parse((event as MessageEvent).data) as {
        scanners: ScannerStreamInfo[];
        sentAt?: string;
      };
      // Snapshot is authoritative — replace, don't merge. Drops scanners
      // that left the org since the previous snapshot.
      scannersByImei.clear();
      for (const s of payload.scanners ?? []) {
        if (s?.imei) scannersByImei.set(s.imei, s);
      }
      lastSnapshotAt = Date.now();
      setStatus("live");
      emit();
    } catch (err) {
      console.warn("[scannerStream] bad scanner_state payload:", err);
    }
  });

  next.addEventListener("scanner_checkin", (event) => {
    if (es !== next) return;
    try {
      const payload = JSON.parse((event as MessageEvent).data) as ScannerStreamInfo;
      if (!payload?.imei) return;
      scannersByImei.set(payload.imei, payload);
      emit();
    } catch (err) {
      console.warn("[scannerStream] bad scanner_checkin payload:", err);
    }
  });

  next.onopen = () => {
    if (es !== next) return;
    // 'live' fires on the first scanner_state event; opening the socket
    // alone isn't enough because the snapshot may take a tick.
    if (status === "reconnecting" && scannersByImei.size > 0) {
      setStatus("live");
    }
  };

  next.onerror = () => {
    if (es !== next) return;
    // Browser auto-retries every `retry: 3000` ms; surface that to the UI.
    // If the token expired (401), the browser will hit error → reconnect →
    // 401 in a loop. The onIdTokenChanged listener forces a fresh token.
    setStatus(scannersByImei.size > 0 ? "reconnecting" : "offline");
  };
}

export function start(orgId: string | number): void {
  const next = String(orgId);
  if (currentOrgId === next && es) return; // already connected to this org
  currentOrgId = next;
  scannersByImei.clear();
  lastSnapshotAt = null;

  if (!unsubscribeIdToken) {
    // Firebase rotates the ID token roughly every 50 min. The current
    // EventSource is bound to the URL we built with the old token, so the
    // next network blip would auto-retry against an expired token → 401.
    // Force a fresh connection on rotation.
    unsubscribeIdToken = auth.onIdTokenChanged(() => {
      if (currentOrgId) connect();
    });
  }

  void connect();
}

export function stop(): void {
  connectGeneration++;
  currentOrgId = null;
  if (es) {
    try {
      es.close();
    } catch {
      // already closed
    }
    es = null;
  }
  if (unsubscribeIdToken) {
    unsubscribeIdToken();
    unsubscribeIdToken = null;
  }
  scannersByImei.clear();
  lastSnapshotAt = null;
  setStatus("idle");
}

export function subscribe(fn: Subscriber): () => void {
  subscribers.add(fn);
  // Replay current snapshot immediately so late subscribers don't sit empty
  // until the next event arrives.
  fn({
    scannersByImei: new Map(scannersByImei),
    status,
    lastSnapshotAt,
  });
  return () => {
    subscribers.delete(fn);
  };
}

export function getCurrentSnapshot(): ScannerStreamSnapshot {
  return {
    scannersByImei: new Map(scannersByImei),
    status,
    lastSnapshotAt,
  };
}
